import { Icon, type IconName, Pill } from "@/components/ui";
import { type Theme, applyTheme, getAppliedTheme, nextTheme, persistTheme } from "@/lib/theme";
import { useIsMobile } from "@/lib/useViewport";
// Shell.tsx — app sidebar + top bar.
import { useState } from "react";

const iconBtnStyle = (active?: boolean): React.CSSProperties => ({
  width: 40,
  height: 40,
  flexShrink: 0,
  borderRadius: "50%",
  background: active ? "var(--brand)" : "var(--bg-surface-2)",
  border: 0,
  color: active ? "#ffffff" : "var(--text-base)",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
});

export type ScreenId =
  | "dashboard"
  | "list"
  | "new"
  | "reports"
  | "master"
  | "cost"
  | "projection"
  | "report"
  | "feedback"
  | "superadmin";

interface NavItem {
  id: ScreenId;
  label: string;
  en: string;
  icon: IconName;
}

export const NAV: NavItem[] = [
  { id: "dashboard", label: "대시보드", en: "Dashboard", icon: "home" },
  { id: "list", label: "견적 리스트", en: "Quotes", icon: "list" },
  { id: "new", label: "견적 등록", en: "New Quote", icon: "plus" },
  { id: "reports", label: "통계 · 보고", en: "Reports", icon: "chart" },
  { id: "master", label: "고객사 정보", en: "Customer Info", icon: "db" },
  { id: "cost", label: "원가 마스터", en: "Cost Master", icon: "layers" },
  { id: "projection", label: "다년 손익", en: "Projection", icon: "chart" },
  { id: "report", label: "원가 출력", en: "Report", icon: "file" },
  // 슈퍼어드민 전용 — AppSidebar 의 showSuperadmin 플래그가 없으면 숨겨진다.
  { id: "superadmin", label: "슈퍼어드민", en: "Superadmin", icon: "eye" },
];

// CNC 견적 집중 기간 동안 NAV/탑바에서 숨기는 화면. 코드·라우팅은 유지되며
// 집합에서 id 를 빼면 즉시 복구된다(단일 소스). feedback 은 탑바 버튼이라 App.tsx 에서 가드.
export const HIDDEN_SCREENS = new Set<ScreenId>(["reports", "projection", "report", "feedback"]);

export interface ShellUser {
  name: string;
  role: string;
  dept: string;
  initial: string;
}

const SideNavItem = ({
  item,
  active,
  onClick,
}: {
  item: NavItem;
  active: boolean;
  onClick: () => void;
}) => {
  const [h, setH] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 13,
        padding: "11px 14px",
        borderRadius: 12,
        cursor: "pointer",
        color: active ? "var(--text-base)" : h ? "var(--text-base)" : "var(--text-muted)",
        background: active ? "var(--bg-surface-2)" : h ? "var(--bg-hover-faint)" : "transparent",
        fontWeight: active ? 700 : 500,
        fontSize: 14.5,
        transition: "color 120ms, background 120ms cubic-bezier(.3,0,0,1)",
        userSelect: "none",
        position: "relative",
      }}
    >
      <Icon
        name={item.icon}
        size={19}
        stroke={active ? 2.4 : 2}
        color={active ? "var(--brand)" : "currentColor"}
      />
      {/* 라벨 개행 금지("고객사 정보" 등) — 좁을 땐 우측 en 캡션이 대신 줄어든다. */}
      <span style={{ flex: 1, whiteSpace: "nowrap" }}>{item.label}</span>
      <span
        style={{
          fontSize: 10.5,
          color: active ? "var(--text-fainter)" : "var(--text-ghost)",
          letterSpacing: "0.5px",
          textTransform: "uppercase",
          fontWeight: 700,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {item.en}
      </span>
    </div>
  );
};

export const AppSidebar = ({
  active,
  onNavigate,
  user,
  onProfileClick,
  showSuperadmin,
  style,
}: {
  active: ScreenId;
  onNavigate: (id: ScreenId) => void;
  user: ShellUser;
  onProfileClick: () => void;
  showSuperadmin?: boolean;
  style?: React.CSSProperties;
}) => (
  <aside
    style={{
      width: 264,
      flexShrink: 0,
      background: "var(--bg-shell)",
      padding: 8,
      display: "flex",
      flexDirection: "column",
      gap: 8,
      ...style,
    }}
  >
    <div style={{ background: "var(--bg-base)", borderRadius: 16, padding: "18px 10px 14px" }}>
      <div style={{ padding: "0 12px 14px", display: "flex", alignItems: "center", gap: 11 }}>
        <img
          src="/logo.png"
          alt="한부금속"
          width={32}
          height={32}
          style={{ borderRadius: 10, display: "block" }}
        />
        <div style={{ lineHeight: 1.1 }}>
          <div style={{ fontWeight: 900, fontSize: 16, letterSpacing: "-0.01em" }}>
            한부금속<span style={{ color: "var(--brand)" }}>.</span>
          </div>
          <div
            style={{
              fontSize: 10,
              color: "var(--text-faint)",
              letterSpacing: "1.6px",
              fontWeight: 700,
              marginTop: 1,
            }}
          >
            CMS
          </div>
        </div>
      </div>
      {NAV.filter(
        (it) => !HIDDEN_SCREENS.has(it.id) && (it.id !== "superadmin" || showSuperadmin)
      ).map((it) => (
        <SideNavItem
          key={it.id}
          item={it}
          active={active === it.id}
          onClick={() => onNavigate(it.id)}
        />
      ))}
    </div>
    <div
      style={{
        background: "var(--bg-base)",
        borderRadius: 16,
        padding: 16,
        flex: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: "var(--text-muted)",
          textTransform: "uppercase",
          letterSpacing: "1.6px",
          marginBottom: 12,
        }}
      >
        빠른 작업
      </div>
      <Pill
        variant="primary"
        size="md"
        icon={<Icon name="plus" size={17} />}
        onClick={() => onNavigate("new")}
        style={{ width: "100%", marginBottom: 8 }}
      >
        새 견적 작성
      </Pill>
      <div style={{ flex: 1 }} />
      <button
        type="button"
        onClick={onProfileClick}
        title="개인 정보 설정"
        aria-label="개인 정보 설정"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 8px",
          borderTop: "1px solid var(--bg-card)",
          borderLeft: 0,
          borderRight: 0,
          borderBottom: 0,
          background: "transparent",
          width: "100%",
          textAlign: "left",
          font: "inherit",
          color: "inherit",
          cursor: "pointer",
          borderRadius: 0,
        }}
      >
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            background: "var(--brand)",
            color: "#ffffff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 800,
            fontSize: 14,
            flexShrink: 0,
          }}
        >
          {user.initial}
        </div>
        <div style={{ lineHeight: 1.25, flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13.5,
              fontWeight: 700,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {user.name}
          </div>
          <div style={{ fontSize: 11.5, color: "var(--text-faint)" }}>
            {user.role} · {user.dept}
          </div>
        </div>
        <Icon name="settings" size={15} color="var(--text-faint)" />
      </button>
    </div>
  </aside>
);

export const AppTopBar = ({
  title,
  query,
  setQuery,
  onLogout,
  crumbs,
  action,
  onFeedback,
  feedbackActive,
  onMenu,
}: {
  title: string;
  query: string;
  setQuery: (v: string) => void;
  onLogout: () => void;
  crumbs?: string;
  action?: { icon: IconName; label: string; onClick: () => void };
  onFeedback?: () => void;
  feedbackActive?: boolean;
  // Mobile only: opens the nav drawer. When set, a hamburger renders at far left.
  onMenu?: () => void;
}) => {
  const [f, setF] = useState(false);
  const [theme, setTheme] = useState<Theme>(getAppliedTheme);
  const mobile = useIsMobile();
  const toggleTheme = () => {
    const t = nextTheme(theme);
    setTheme(t);
    applyTheme(t);
    persistTheme(t);
  };
  return (
    <header
      style={{
        minHeight: 64,
        padding: mobile ? "10px 14px" : "0 28px",
        display: "flex",
        alignItems: "center",
        gap: mobile ? 10 : 18,
        flexWrap: mobile ? "wrap" : "nowrap",
        background: "var(--bg-topbar)",
        backdropFilter: "blur(16px) saturate(140%)",
        WebkitBackdropFilter: "blur(16px) saturate(140%)",
        position: "sticky",
        top: 0,
        zIndex: 20,
        borderBottom: "1px solid var(--bg-card-hover)",
      }}
    >
      {onMenu && (
        <button
          type="button"
          onClick={onMenu}
          title="메뉴"
          aria-label="메뉴"
          style={iconBtnStyle()}
        >
          <Icon name="list" size={20} />
        </button>
      )}
      <div style={{ minWidth: 0, flex: mobile ? 1 : undefined }}>
        {crumbs && !mobile && (
          <div style={{ fontSize: 11.5, color: "var(--text-faint)", marginBottom: 2 }}>
            {crumbs}
          </div>
        )}
        <div
          style={{
            fontFamily: "var(--font-title)",
            fontWeight: 800,
            fontSize: mobile ? 17 : 19,
            letterSpacing: "-0.01em",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {title}
        </div>
      </div>
      {!mobile && <div style={{ flex: 1 }} />}
      <div
        style={{
          position: "relative",
          width: mobile ? "100%" : 300,
          order: mobile ? 9 : undefined,
          flexBasis: mobile ? "100%" : undefined,
          height: 40,
          background: "var(--bg-surface-2)",
          borderRadius: 500,
          padding: "0 18px 0 42px",
          display: "flex",
          alignItems: "center",
          boxShadow: f
            ? "var(--ring-focus) 0px 0px 0px 2px inset"
            : "var(--ring-input) 0px 0px 0px 1px inset",
          transition: "box-shadow 120ms",
        }}
      >
        <span
          style={{
            position: "absolute",
            left: 15,
            color: f ? "var(--text-base)" : "var(--text-muted)",
            display: "flex",
          }}
        >
          <Icon name="search" size={17} />
        </span>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setF(true)}
          onBlur={() => setF(false)}
          placeholder="견적번호 · 고객사 · 품목 검색"
          style={{
            background: "transparent",
            border: 0,
            outline: 0,
            color: "var(--text-base)",
            fontSize: 13.5,
            width: "100%",
            fontFamily: "inherit",
          }}
        />
      </div>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          title={action.label}
          aria-label={action.label}
          style={iconBtnStyle(true)}
        >
          <Icon name={action.icon} size={18} />
        </button>
      )}
      {onFeedback && (
        <button
          type="button"
          onClick={onFeedback}
          title="개선 · 이슈"
          aria-label="개선 · 이슈"
          style={iconBtnStyle(feedbackActive)}
        >
          <Icon name="message" size={18} />
        </button>
      )}
      <button
        type="button"
        onClick={toggleTheme}
        title={theme === "dark" ? "라이트 모드로 전환" : "다크 모드로 전환"}
        aria-label={theme === "dark" ? "라이트 모드로 전환" : "다크 모드로 전환"}
        style={iconBtnStyle()}
      >
        <Icon name={theme === "dark" ? "sun" : "moon"} size={18} />
      </button>
      <button
        type="button"
        onClick={onLogout}
        title="로그아웃"
        style={{ ...iconBtnStyle(), color: "var(--text-muted)" }}
      >
        <Icon name="logout" size={17} />
      </button>
    </header>
  );
};
